import Te from "@arcgis/core/identity/IdentityManager";
import Qr from "@arcgis/core/identity/OAuthInfo";
import H from "@arcgis/core/portal/Portal";
import { createLogger as x } from "@origo/logger";
import ar from "@arcgis/core/portal/PortalQueryParams";
import Ee from "@arcgis/core/portal/PortalItem";
import Ce from "@arcgis/core/request";
import Xr from "@arcgis/core/rest/support/Query";
import * as Hr from "@arcgis/core/rest/query";
import Z, { useEffect as $, useRef as Ae, useCallback as Zr, useState as F, Component as et } from "react";
import { useDispatch as Oe, useSelector as j } from "react-redux";
import { createAsyncThunk as q, createSlice as Ie, combineReducers as rt } from "@reduxjs/toolkit";
import { CalciteAlert as tt, CalciteButton as nt } from "@esri/calcite-components-react";
const N = x("AuthService");
class or {
  constructor(e) {
    this.config = {
      appId: e.appId,
      portalUrl: e.portalUrl || "https://www.arcgis.com",
      flowType: e.flowType || "auto",
      popup: e.popup ?? !1
    };
  }
  /**
   * Initializes OAuth configuration and registers with IdentityManager
   * 
   * @example
   * ```typescript
   * const authService = new AuthService({
   *   appId: '06bJzGl3cUQAR5lX',
   *   portalUrl: 'https://www.arcgis.com'
   * });
   * authService.initialize();
   * ```
   */
  initialize() {
    if (this.oauthInfo) {
      N.debug("Already initialized");
      return;
    }
    this.oauthInfo = new Qr({
      appId: this.config.appId,
      portalUrl: this.config.portalUrl,
      flowType: this.config.flowType,
      popup: this.config.popup
    }), Te.registerOAuthInfos([this.oauthInfo]), N.debug(`Initialized OAuth for ${this.config.portalUrl}`);
  }
  /**
   * Checks current sign-in status and retrieves user information
   * 
   * @param requireAuth - If true, forces sign-in if not authenticated
   * @returns Object with sign-in status and user info
   * 
   * @example
   * ```typescript
   * // Check status without forcing sign-in (for public apps)
   * const { isSignedIn, user } = await authService.checkSignInStatus(false);
   * 
   * // Force sign-in (for authenticated apps)
   * const { isSignedIn, user } = await authService.checkSignInStatus(true);
   * ```
   */
  async checkSignInStatus(e = !1) {
    try {
      const t = new H({ url: this.config.portalUrl });
      if (t.authMode = e ? "immediate" : "auto", await t.load(), t.user) {
        const i = {
          username: t.user.username,
          fullName: t.user.fullName || void 0,
          email: t.user.email || void 0,
          role: t.user.role || void 0,
          thumbnail: t.user.thumbnailUrl || void 0,
          orgUrl: t.url || void 0
        };
        return N.info(`User signed in: ${i.username}`), { isSignedIn: !0, user: i };
      }
      return N.debug("User not signed in"), { isSignedIn: !1 };
    } catch (t) {
      if (N.error("Error checking sign-in status:", t), e)
        throw new Error("Authentication required but sign-in failed");
      return { isSignedIn: !1 };
    }
  }
  /**
   * Forces user sign-in
   * 
   * Used by authenticated apps (e.g., drømmeplan-admin) to require authentication.
   * Will trigger OAuth flow if user is not signed in.
   * 
   * @returns User information after successful sign-in
   * @throws Error if sign-in fails
   * 
   * @example
   * ```typescript
   * try {
   *   const user = await authService.requireSignIn();
   *   console.log('Signed in as:', user.username);
   * } catch (error) {
   *   console.error('Sign-in failed:', error);
   * }
   * ```
   */
  async requireSignIn() {
    const e = await this.checkSignInStatus(!0);
    if (!e.isSignedIn || !e.user)
      throw new Error("Sign-in required but user not authenticated");
    return e.user;
  }
  /**
   * Signs out current user
   * 
   * @example
   * ```typescript
   * await authService.signOut();
   * ```
   */
  async signOut() {
    try {
      await Te.destroyCredentials(), N.debug("User signed out");
    } catch (e) {
      throw N.error("Error signing out:", e), e;
    }
  }
  /**
   * Checks whether the signed-in user is a member of a Portal group
   *
   * @param groupIdOrTitle - AGOL group id or title to match against
   * @returns True if the user belongs to a group matching the id or title
   */
  async isMemberOfGroup(e) {
    const i = await (await this.getAuthenticatedPortal()).user.fetchGroups(), o = e.trim().toLowerCase();
    return (i || []).some(
      (p) => p.id === e || (p.title || "").trim().toLowerCase() === o
    );
  }
  /**
   * Gets current portal instance
   *
   * @returns Portal instance configured for this auth service
   */
  getPortal() {
    return new H({ url: this.config.portalUrl });
  }
  /**
   * Gets an authenticated and loaded Portal instance with user context.
   * 
   * Caches the loaded portal for subsequent calls to avoid repeated auth round-trips.
   * Requires that IdentityManager has been initialized with OAuth credentials
   * (i.e., the user has signed in via `checkSignInStatus` or `requireSignIn` first).
   * 
   * @returns Loaded Portal instance with `portal.user` available
   * @throws Error if portal cannot be loaded or user is not authenticated
   * 
   * @example
   * ```typescript
   * const portal = await authService.getAuthenticatedPortal();
   * console.log('Authenticated as:', portal.user.username);
   * ```
   */
  async getAuthenticatedPortal() {
    if (this.cachedPortal?.user)
      return this.cachedPortal;
    const e = new H({ url: this.config.portalUrl });
    if (e.authMode = "immediate", await e.load(), !e.user)
      throw new Error("Portal loaded but no authenticated user found");
    return this.cachedPortal = e, e;
  }
}
class _ extends Error {
  constructor(e, t, i) {
    super(e), this.name = "SettingsError", this.errorType = t, Object.assign(this, i);
  }
  /**
   * Creates user-friendly Norwegian error message
   */
  toUserMessage() {
    switch (this.errorType) {
      case "NOT_FOUND":
        return `Kunne ikke finne innstillinger for ${this.itemName || "applikasjonen"}. Kontakt administrator.`;
      case "NETWORK":
        return "Kunne ikke laste innstillinger på grunn av nettverksfeil. Prøv igjen.";
      case "PARSE":
        return "Innstillingene har ugyldig format. Kontakt administrator.";
      case "UNAUTHORIZED":
        return "Du har ikke tilgang til å laste innstillinger. Kontakt administrator.";
      default:
        return "En ukjent feil oppstod ved lasting av innstillinger. Kontakt administrator.";
    }
  }
}
const ae = x("Auth");
let K = null;
function Pe() {
  if (K !== null)
    return K;
  const n = window.location.hostname;
  if (n === "localhost" || n === "127.0.0.1") {
    const t = typeof import.meta < "u" && { BASE_URL: "/", MODE: "production", DEV: !1, PROD: !0, SSR: !1 }.VITE_DEV_CUSTOMER, i = t || "mars";
    return t ? ae.debug(`Development mode: using VITE_DEV_CUSTOMER='${i}'`) : ae.debug(`Development mode: using default customer '${i}'. Set VITE_DEV_CUSTOMER to override.`), K = i, i;
  }
  const e = n.split(".");
  if (e.length >= 2) {
    const t = e[1];
    return ae.debug(`Extracted customer domain from hostname: '${t}'`), K = t, t;
  }
  return ae.warn(`Unexpected hostname format: '${n}'. Using hostname as customer domain.`), K = n, n;
}
function st(n) {
  const e = Pe();
  return `title:"${n}.tokenservice", tags:"${n}.tokenservice@${e}"`;
}
function it(n) {
  const e = Pe();
  return `title:"${n}.settings", tags:"${n}.settings@${e}"`;
}
const P = x("SettingsService");
class ur {
  constructor(e = {}) {
    this.portalUrl = e.portalUrl || "https://www.arcgis.com", this.timeout = e.timeout || 1e4;
  }
  /**
   * Loads app settings from portal item
   * 
   * @param config - Settings configuration
   * @param requireAuth - Whether authentication is required for loading settings
   * @returns Loaded settings
   * @throws SettingsError if the settings item cannot be found, accessed, or parsed
   *
   * @example
   * ```typescript
   * const settingsService = new SettingsService();
   *
   * try {
   *   const settings = await settingsService.loadSettings({
   *     appName: 'drømmeplan',
   *     defaultSettings: { webmap: 'default-id' }
   *   }, false);
   * } catch (error) {
   *   if (error instanceof SettingsError) {
   *     alert(error.toUserMessage());
   *   }
   * }
   * ```
   */
  async loadSettings(e, t = !1) {
    const i = it(e.appName), o = Pe();
    P.debug(`Loading settings: ${i}`);
    let p;
    try {
      const d = new H({ url: this.portalUrl });
      d.authMode = t ? "immediate" : "anonymous", await d.load();
      const l = new ar({
        query: i
      }), g = new Promise((w, O) => {
        p = setTimeout(() => {
          O(new _(
            `Settings query timed out after ${this.timeout}ms`,
            "NETWORK",
            {
              itemName: i,
              customer: o,
              portalUrl: this.portalUrl,
              technicalDetails: `Query timeout: ${this.timeout}ms`
            }
          ));
        }, this.timeout);
      }), S = await Promise.race([
        d.queryItems(l),
        g
      ]);
      clearTimeout(p);
      const v = S.results;
      if (v.length === 0)
        throw P.warn(`Settings item not found: ${i}`), new _(
          `Settings item not found: ${i}`,
          "NOT_FOUND",
          { itemName: i, customer: o, portalUrl: this.portalUrl }
        );
      const y = v[0];
      P.debug(`Found settings item: ${y.id}`);
      try {
        const w = await y.fetchData();
        if (!w || typeof w != "object")
          throw P.warn(`Settings data has invalid format. Expected object, got ${typeof w}`), new _(
            `Settings data has invalid format for ${i}`,
            "PARSE",
            { itemName: i, customer: o, portalUrl: this.portalUrl }
          );
        return P.debug("Settings loaded successfully"), w;
      } catch (w) {
        throw w instanceof _ ? w : (P.warn("Failed to fetch settings data from item:", w), new _(
          `Failed to fetch settings data for ${i}`,
          "PARSE",
          {
            itemName: i,
            customer: o,
            portalUrl: this.portalUrl,
            technicalDetails: w instanceof Error ? w.message : String(w)
          }
        ));
      }
    } catch (d) {
      throw clearTimeout(p), d instanceof _ ? (P.warn("Failed to load settings:", d), d) : (P.warn("Error loading settings:", d), new _(
        `Unexpected error loading settings: ${i}`,
        "UNKNOWN",
        {
          itemName: i,
          customer: o,
          portalUrl: this.portalUrl,
          technicalDetails: d instanceof Error ? d.message : String(d)
        }
      ));
    }
  }
}
const J = x("PortalItemService");
class Ft {
  constructor(e) {
    this.portal = e;
  }
  /**
   * Creates a new Portal item with JSON data
   * 
   * @param data - JSON-serializable data to store
   * @param metadata - Item title, tags, and optional description
   * @returns The ID of the created item
   */
  async createItem(e, t) {
    const i = new Ee({
      portal: this.portal,
      title: t.title,
      type: t.type || "Web Mapping Application",
      tags: t.tags,
      snippet: t.snippet,
      description: t.description,
      typeKeywords: ["origo", "filter-template"]
    });
    if (!this.portal.user)
      throw new Error("No authenticated user on portal");
    const o = await this.portal.user.addItem({
      item: i,
      data: JSON.stringify(e)
    });
    if (!o?.id)
      throw new Error("Failed to create Portal item - no ID returned");
    return J.debug(`Created item: ${o.id}`), o.id;
  }
  /**
   * Updates an existing Portal item's data and metadata
   * 
   * @param itemId - ID of the item to update
   * @param data - New JSON data
   * @param metadata - Optional updated metadata
   */
  async updateItem(e, t, i) {
    const o = new Ee({
      portal: this.portal,
      id: e
    });
    await o.load(), i?.title && (o.title = i.title), i?.snippet && (o.snippet = i.snippet), i?.description && (o.description = i.description), i?.tags && (o.tags = i.tags), await o.update({ data: JSON.stringify(t) }), J.debug(`Updated item: ${e}`);
  }
  /**
   * Deletes a Portal item
   * 
   * @param itemId - ID of the item to delete
   */
  async deleteItem(e) {
    if (!this.portal.user)
      throw new Error("No authenticated user on portal");
    const t = `${this.portal.restUrl}/content/users/${this.portal.user.username}/items/${e}/delete`;
    await Ce(t, {
      method: "post",
      query: { f: "json" },
      authMode: "immediate"
    }), J.debug(`Deleted item: ${e}`);
  }
  /**
   * Queries Portal items by tags. Returns own items and items shared
   * with the user's groups.
   * 
   * @param tags - Tags to filter by (all must match)
   * @returns Array of item results with their data
   */
  async queryItems(e) {
    const i = `${e.map((l) => `tags:"${l}"`).join(" AND ")} AND type:"Web Mapping Application"`, o = await this.portal.queryItems({
      query: i,
      num: 100,
      sortField: "modified",
      sortOrder: "desc"
    }), p = await Promise.allSettled(
      o.results.map(async (l) => {
        const g = await l.fetchData();
        return {
          id: l.id,
          title: l.title,
          snippet: l.snippet || void 0,
          owner: l.owner,
          created: l.created?.getTime() ?? 0,
          modified: l.modified?.getTime() ?? 0,
          tags: l.tags || [],
          data: g
        };
      })
    ), d = [];
    for (const l of p)
      l.status === "fulfilled" ? d.push(l.value) : J.warn("Failed to fetch data for an item:", l.reason);
    return d;
  }
  /**
   * Shares a Portal item with specified groups
   * 
   * @param itemId - ID of the item to share
   * @param groupIds - Array of group IDs to share with
   */
  async shareToGroups(e, t) {
    if (await new Ee({
      portal: this.portal,
      id: e
    }).load(), !this.portal.user)
      throw new Error("No authenticated user on portal");
    const o = `${this.portal.restUrl}/content/users/${this.portal.user.username}/items/${e}/share`;
    await Ce(o, {
      method: "post",
      query: {
        everyone: !1,
        org: !1,
        groups: t.join(","),
        f: "json"
      },
      authMode: "immediate"
    }), J.debug(`Shared item ${e} with groups: ${t.join(", ")}`);
  }
  /**
   * Gets the authenticated user's Portal groups, useful for showing
   * sharing options in the UI.
   * 
   * @returns Array of groups with id and title
   */
  async getUserGroups() {
    const e = this.portal.user;
    if (!e)
      throw new Error("No authenticated user on portal");
    return (await e.fetchGroups() || []).map((i) => ({
      id: i.id || "",
      title: i.title || ""
    }));
  }
}
const ke = x("GdoTokenProvider");
class lr {
  constructor(e, t = {}) {
    this.refreshCallbacks = [], this.refreshWaiters = [], this.isRefreshing = !1, this.appName = e, this.requireAuth = t.requireAuth ?? !1, this.portalUrl = t.portalUrl || "https://www.arcgis.com", this.refreshConfig = {
      refreshBufferMinutes: t.refreshConfig?.refreshBufferMinutes ?? 5,
      maxRetries: t.refreshConfig?.maxRetries ?? 3,
      retryBaseDelay: t.refreshConfig?.retryBaseDelay ?? 30
    };
  }
  async getToken() {
    if (this.currentToken && !this.isExpired())
      return this.refreshTimer || this.scheduleRefresh(), this.currentToken;
    try {
      return await this.fetchTokenFromService();
    } catch (e) {
      ke.warn("Failed:", e);
      return;
    }
  }
  async refreshToken() {
    if (this.isRefreshing)
      return new Promise((e, t) => {
        const i = setTimeout(() => {
          t(new Error("Token refresh timed out waiting for in-flight refresh"));
        }, 3e4);
        this.refreshWaiters.push((o) => {
          clearTimeout(i), o ? e(o) : t(new Error("Token refresh failed"));
        });
      });
    this.isRefreshing = !0;
    try {
      const e = await this.fetchTokenFromService();
      return this.isRefreshing = !1, this.drainRefreshWaiters(e), this.refreshCallbacks.forEach((t) => t(e)), e;
    } catch (e) {
      throw this.isRefreshing = !1, this.drainRefreshWaiters(void 0), this.refreshCallbacks.forEach((t) => t(void 0)), e;
    }
  }
  drainRefreshWaiters(e) {
    const t = this.refreshWaiters;
    this.refreshWaiters = [], t.forEach((i) => i(e));
  }
  getExpiration() {
    return this.expiresAt;
  }
  isExpired() {
    if (!this.expiresAt)
      return !1;
    const e = Date.now(), t = this.refreshConfig.refreshBufferMinutes * 60 * 1e3;
    return e >= this.expiresAt - t;
  }
  onTokenRefreshed(e) {
    return this.refreshCallbacks.push(e), () => {
      this.refreshCallbacks = this.refreshCallbacks.filter((t) => t !== e);
    };
  }
  cleanup() {
    this.refreshTimer && (clearTimeout(this.refreshTimer), this.refreshTimer = void 0), this.refreshCallbacks = [];
  }
  async fetchTokenFromService() {
    const e = st(this.appName), t = new ar({ query: e }), i = new H({ url: this.portalUrl });
    this.requireAuth || (i.authMode = "anonymous");
    const p = (await i.queryItems(t)).results;
    if (p.length === 0)
      throw new Error("Token service not found");
    const d = new Xr({ where: "1=1", outFields: ["*"], returnGeometry: !1 }), l = await Hr.executeQueryJSON(p[0].url, d);
    if (l.features.length === 0)
      throw new Error("No token in service");
    const g = l.features[0].attributes, S = g.token;
    if (!S)
      throw new Error("Token attribute empty");
    this.currentToken = S;
    const v = g.expires || g.expiresAt || g.expiration;
    return v ? (this.expiresAt = typeof v == "number" ? v : Date.parse(v), this.scheduleRefresh()) : (this.expiresAt = Date.now() + 24 * 60 * 60 * 1e3, this.scheduleRefresh()), S;
  }
  scheduleRefresh() {
    if (this.refreshTimer && clearTimeout(this.refreshTimer), !this.expiresAt)
      return;
    const e = Date.now(), t = this.refreshConfig.refreshBufferMinutes * 60 * 1e3, i = this.expiresAt - t - e, o = this.refreshConfig.retryBaseDelay * 1e3;
    i < o && ke.warn(`Token utløper om ${Math.round(i / 1e3)} s (inkl. buffer) — neste refresh om ${this.refreshConfig.retryBaseDelay} s`), this.refreshTimer = window.setTimeout(() => {
      this.refreshTimer = void 0, this.refreshWithRetry();
    }, Math.max(i, o));
  }
  async refreshWithRetry(e = 1) {
    try {
      await this.refreshToken();
    } catch {
      if (e < this.refreshConfig.maxRetries) {
        const i = this.refreshConfig.retryBaseDelay * Math.pow(2, e - 1);
        this.refreshTimer = window.setTimeout(() => {
          this.refreshTimer = void 0, this.refreshWithRetry(e + 1);
        }, i * 1e3);
      } else
        ke.error("Token refresh failed after max retries");
    }
  }
  async queryRoles(e) {
    try {
      return (await Ce("https://services.geodataonline.no/arcgis/rest/services/ProxyServices/roles", { query: { token: e } })).data;
    } catch {
      return {};
    }
  }
  async getTokenMetadata() {
    const e = await this.getToken();
    if (!e)
      return;
    const t = await this.queryRoles(e);
    return { token: e, expiresAt: this.expiresAt, username: t.username, customerName: t.customerName, applicationRoles: t.applicationRoles };
  }
}
const at = {
  isSignedIn: !1,
  user: void 0,
  status: "idle",
  error: void 0,
  isAuthRequired: !1
}, oe = q(
  "auth/checkSignInStatus",
  async ({ authService: n, requireAuth: e }, { rejectWithValue: t }) => {
    try {
      return await n.checkSignInStatus(e);
    } catch (i) {
      return t(i.message);
    }
  }
), ue = q(
  "auth/requireSignIn",
  async ({ authService: n }, { rejectWithValue: e }) => {
    try {
      return await n.requireSignIn();
    } catch (t) {
      return e(t.message);
    }
  }
), le = q(
  "auth/signOut",
  async ({ authService: n }, { rejectWithValue: e }) => {
    try {
      await n.signOut();
    } catch (t) {
      return e(t.message);
    }
  }
), cr = Ie({
  name: "auth",
  initialState: at,
  reducers: {
    setIsAuthRequired: (n, e) => {
      n.isAuthRequired = e.payload;
    },
    clearAuth: (n) => {
      n.isSignedIn = !1, n.user = void 0, n.status = "idle", n.error = void 0;
    }
  },
  extraReducers: (n) => {
    n.addCase(oe.pending, (e) => {
      e.status = "loading", e.error = void 0;
    }).addCase(oe.fulfilled, (e, t) => {
      e.status = "succeeded", e.isSignedIn = t.payload.isSignedIn, e.user = t.payload.user;
    }).addCase(oe.rejected, (e, t) => {
      e.status = "failed", e.error = t.payload;
    }).addCase(ue.pending, (e) => {
      e.status = "loading", e.error = void 0;
    }).addCase(ue.fulfilled, (e, t) => {
      e.status = "succeeded", e.isSignedIn = !0, e.user = t.payload;
    }).addCase(ue.rejected, (e, t) => {
      e.status = "failed", e.error = t.payload;
    }).addCase(le.pending, (e) => {
      e.status = "loading";
    }).addCase(le.fulfilled, (e) => {
      e.status = "idle", e.isSignedIn = !1, e.user = void 0, e.error = void 0;
    }).addCase(le.rejected, (e, t) => {
      e.status = "failed", e.error = t.payload;
    });
  }
}), { setIsAuthRequired: Mt, clearAuth: Wt } = cr.actions, ot = cr.reducer, ut = (n) => n.auth.auth.isSignedIn, lt = (n) => n.auth.auth.user, ct = (n) => n.auth.auth.status, qt = (n) => n.auth.auth.error, Lt = (n) => n.auth.auth.isAuthRequired;
function fr(n, e = !1) {
  const t = Oe(), i = j(ut), o = j(lt), p = j(ct);
  return $(() => {
    t(oe({ authService: n, requireAuth: e }));
  }, [t, n, e]), { isSignedIn: i, user: o, isLoading: p === "loading", signIn: async () => {
    await t(ue({ authService: n })).unwrap();
  }, signOut: async () => {
    await t(le({ authService: n })).unwrap(), window.location.reload();
  } };
}
const dr = x("tokenSlice"), ft = {
  token: void 0,
  expiresAt: void 0,
  isRefreshing: !1,
  lastRefreshError: void 0,
  appName: void 0,
  username: void 0,
  customerName: void 0,
  applicationRoles: []
}, ce = q("token/getToken", async ({ tokenProvider: n }) => {
  try {
    return await n.getTokenMetadata?.();
  } catch (e) {
    dr.warn("Failed to get token:", e);
    return;
  }
}), Se = q(
  "token/refreshToken",
  async ({ tokenProvider: n }) => {
    try {
      return await n.refreshToken(), await n.getTokenMetadata?.();
    } catch (e) {
      dr.warn("Failed to refresh token:", e);
      return;
    }
  }
), hr = Ie({
  name: "token",
  initialState: ft,
  reducers: {
    setToken: (n, e) => {
      n.token = e.payload;
    },
    setTokenMetadata: (n, e) => {
      e.payload.token && (n.token = e.payload.token), e.payload.expiresAt && (n.expiresAt = e.payload.expiresAt), e.payload.username && (n.username = e.payload.username), e.payload.customerName && (n.customerName = e.payload.customerName), e.payload.applicationRoles && (n.applicationRoles = e.payload.applicationRoles);
    },
    setAppName: (n, e) => {
      n.appName = e.payload;
    },
    clearToken: (n) => {
      n.token = void 0, n.expiresAt = void 0, n.lastRefreshError = void 0, n.username = void 0, n.customerName = void 0, n.applicationRoles = [];
    }
  },
  extraReducers: (n) => {
    n.addCase(ce.pending, (e) => {
      e.isRefreshing = !0, e.lastRefreshError = void 0;
    }).addCase(ce.fulfilled, (e, t) => {
      e.isRefreshing = !1, t.payload && (e.token = t.payload.token, e.expiresAt = t.payload.expiresAt, e.username = t.payload.username, e.customerName = t.payload.customerName, e.applicationRoles = t.payload.applicationRoles || []);
    }).addCase(ce.rejected, (e, t) => {
      e.isRefreshing = !1, e.lastRefreshError = t.error?.message;
    }).addCase(Se.pending, (e) => {
      e.isRefreshing = !0, e.lastRefreshError = void 0;
    }).addCase(Se.fulfilled, (e, t) => {
      e.isRefreshing = !1, t.payload && (e.token = t.payload.token, e.expiresAt = t.payload.expiresAt, e.username = t.payload.username, e.customerName = t.payload.customerName, e.applicationRoles = t.payload.applicationRoles || []);
    }).addCase(Se.rejected, (e, t) => {
      e.isRefreshing = !1, e.lastRefreshError = t.error?.message;
    });
  }
}), { setToken: Vt, setTokenMetadata: nr, setAppName: Yt, clearToken: Bt } = hr.actions, dt = hr.reducer, ht = (n) => n.auth.token.token, Gt = (n) => n.auth.token.expiresAt, Kt = (n) => n.auth.token.expiresAt ? Date.now() >= n.auth.token.expiresAt : !1, pt = (n) => n.auth.token.isRefreshing, Jt = (n) => n.auth.token.lastRefreshError, zt = (n) => n.auth.token.token, Qt = (n) => n.auth.token.applicationRoles, Xt = (n) => n.auth.token.username, Ht = (n) => n.auth.token.customerName, gt = x("useTokenProvider");
function pr(n) {
  const e = Oe(), t = j(ht), i = j(pt), o = Ae(!1);
  $(() => {
    const d = n.onTokenRefreshed((l) => {
      e(nr({ token: l, expiresAt: n.getExpiration() }));
    });
    return o.current ? n.getExpiration() !== void 0 && n.getToken().then((l) => {
      l && e(nr({ token: l, expiresAt: n.getExpiration() }));
    }) : (o.current = !0, e(ce({ tokenProvider: n }))), () => {
      d(), n.cleanup();
    };
  }, [e, n]);
  const p = Zr(async () => {
    try {
      await n.refreshToken();
    } catch (d) {
      gt.warn("Token refresh failed:", d);
    }
  }, [n]);
  return { token: t, isRefreshing: i, refresh: p };
}
const mt = x("settingsSlice"), vt = {
  settings: void 0,
  isLoading: !1,
  error: void 0
}, _e = q(
  "settings/loadSettings",
  async ({ settingsService: n, config: e, requireAuth: t }) => {
    try {
      return { settings: await n.loadSettings(e, t) };
    } catch (i) {
      mt.warn("Failed to load settings, using defaults:", i);
      const o = i instanceof _ ? { type: i.errorType, message: i.toUserMessage() } : { type: "UNKNOWN", message: "En ukjent feil oppstod ved lasting av innstillinger." };
      return { settings: e.defaultSettings, error: o };
    }
  }
), gr = Ie({
  name: "settings",
  initialState: vt,
  reducers: {
    setSettings: (n, e) => {
      n.settings = e.payload;
    }
  },
  extraReducers: (n) => {
    n.addCase(_e.pending, (e) => {
      e.isLoading = !0;
    }).addCase(_e.fulfilled, (e, t) => {
      e.isLoading = !1, e.settings = t.payload.settings, e.error = t.payload.error;
    });
  }
}), { setSettings: Zt } = gr.actions, yt = gr.reducer, wt = (n) => n.auth.settings.settings, Rt = (n) => n.auth.settings.isLoading, Et = (n) => n.auth.settings.error;
function mr(n, e, t = !1) {
  const i = Oe(), o = j(wt), p = j(Rt), d = j(Et), l = Ae(!1);
  return $(() => {
    l.current || (l.current = !0, i(_e({ settingsService: n, config: e, requireAuth: t })));
  }, [i, n, t]), { settings: o || e.defaultSettings, isLoading: p, settingsError: d };
}
const be = x("useTokenRegistration");
function en(n, e) {
  $(() => {
    if (n) {
      be.debug(`Registering token for ${e}`);
      try {
        Te.registerToken({
          server: e,
          token: n
        }), be.debug(`Token registered successfully for ${e}`);
      } catch (t) {
        be.error("Failed to register token:", t);
      }
    }
  }, [n, e]);
}
function rn(n, e, t) {
  const [i] = F(() => new or({ appId: "06bJzGl3cUQAR5lX", portalUrl: t })), [o] = F(() => new lr(n, { requireAuth: !1, portalUrl: t })), [p] = F(() => new ur({ portalUrl: t }));
  $(() => {
    i.initialize();
  }, [i]), fr(i, !1);
  const { token: d } = pr(o), { settings: l, isLoading: g, settingsError: S } = mr(p, { appName: n, defaultSettings: e }, !1);
  return { token: d, settings: l, isLoading: g, settingsError: S };
}
const kt = x("useAuthenticatedApp");
function tn(n, e, t) {
  const { portalUrl: i, requiredGroup: o } = t || {}, [p] = F(() => new or({ appId: "06bJzGl3cUQAR5lX", portalUrl: i })), [d] = F(() => new lr(n, { requireAuth: !0, portalUrl: i })), [l] = F(() => new ur({ portalUrl: i })), g = Ae(!1), [S, v] = F(
    o ? "checking" : "skipped"
  );
  $(() => {
    p.initialize();
  }, [p]);
  const y = fr(p, !0), { token: w } = pr(d), { settings: O, isLoading: ee, settingsError: re } = mr(l, { appName: n, defaultSettings: e }, !0);
  $(() => {
    !y.isLoading && !y.isSignedIn && !g.current && (g.current = !0, y.signIn());
  }, [y.isLoading, y.isSignedIn]), $(() => {
    if (!o || !y.isSignedIn)
      return;
    let L = !1;
    return p.isMemberOfGroup(o).then((V) => {
      L || v(V ? "member" : "not-member");
    }).catch((V) => {
      kt.error("Failed to check group membership, denying access:", V), L || v("not-member");
    }), () => {
      L = !0;
    };
  }, [p, o, y.isSignedIn]);
  const fe = !!o && S === "not-member", I = y.isSignedIn && !ee && !!w && (!o || S === "member");
  let E = null;
  return o && S === "checking" || (fe ? E = { kind: "group" } : re && (E = { kind: "settings", error: re })), {
    isSignedIn: y.isSignedIn,
    user: y.user,
    token: w,
    settings: O,
    isReady: I,
    signOut: y.signOut,
    accessError: E
  };
}
var xe = { exports: {} }, z = {};
/**
 * @license React
 * react-jsx-runtime.production.min.js
 *
 * Copyright (c) Facebook, Inc. and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */
var sr;
function St() {
  if (sr)
    return z;
  sr = 1;
  var n = Z, e = Symbol.for("react.element"), t = Symbol.for("react.fragment"), i = Object.prototype.hasOwnProperty, o = n.__SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED.ReactCurrentOwner, p = { key: !0, ref: !0, __self: !0, __source: !0 };
  function d(l, g, S) {
    var v, y = {}, w = null, O = null;
    S !== void 0 && (w = "" + S), g.key !== void 0 && (w = "" + g.key), g.ref !== void 0 && (O = g.ref);
    for (v in g)
      i.call(g, v) && !p.hasOwnProperty(v) && (y[v] = g[v]);
    if (l && l.defaultProps)
      for (v in g = l.defaultProps, g)
        y[v] === void 0 && (y[v] = g[v]);
    return { $$typeof: e, type: l, key: w, ref: O, props: y, _owner: o.current };
  }
  return z.Fragment = t, z.jsx = d, z.jsxs = d, z;
}
var Q = {};
/**
 * @license React
 * react-jsx-runtime.development.js
 *
 * Copyright (c) Facebook, Inc. and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */
var ir;
function bt() {
  return ir || (ir = 1, process.env.NODE_ENV !== "production" && function() {
    var n = Z, e = Symbol.for("react.element"), t = Symbol.for("react.portal"), i = Symbol.for("react.fragment"), o = Symbol.for("react.strict_mode"), p = Symbol.for("react.profiler"), d = Symbol.for("react.provider"), l = Symbol.for("react.context"), g = Symbol.for("react.forward_ref"), S = Symbol.for("react.suspense"), v = Symbol.for("react.suspense_list"), y = Symbol.for("react.memo"), w = Symbol.for("react.lazy"), O = Symbol.for("react.offscreen"), ee = Symbol.iterator, re = "@@iterator";
    function fe(r) {
      if (r === null || typeof r != "object")
        return null;
      var s = ee && r[ee] || r[re];
      return typeof s == "function" ? s : null;
    }
    var I = n.__SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED;
    function E(r) {
      {
        for (var s = arguments.length, a = new Array(s > 1 ? s - 1 : 0), u = 1; u < s; u++)
          a[u - 1] = arguments[u];
        L("error", r, a);
      }
    }
    function L(r, s, a) {
      {
        var u = I.ReactDebugCurrentFrame, h = u.getStackAddendum();
        h !== "" && (s += "%s", a = a.concat([h]));
        var m = a.map(function(f) {
          return String(f);
        });
        m.unshift("Warning: " + s), Function.prototype.apply.call(console[r], console, m);
      }
    }
    var V = !1, vr = !1, yr = !1, wr = !1, Rr = !1, $e;
    $e = Symbol.for("react.module.reference");
    function Er(r) {
      return !!(typeof r == "string" || typeof r == "function" || r === i || r === p || Rr || r === o || r === S || r === v || wr || r === O || V || vr || yr || typeof r == "object" && r !== null && (r.$$typeof === w || r.$$typeof === y || r.$$typeof === d || r.$$typeof === l || r.$$typeof === g || // This needs to include all possible module reference object
      // types supported by any Flight configuration anywhere since
      // we don't know which Flight build this will end up being used
      // with.
      r.$$typeof === $e || r.getModuleId !== void 0));
    }
    function kr(r, s, a) {
      var u = r.displayName;
      if (u)
        return u;
      var h = s.displayName || s.name || "";
      return h !== "" ? a + "(" + h + ")" : a;
    }
    function je(r) {
      return r.displayName || "Context";
    }
    function A(r) {
      if (r == null)
        return null;
      if (typeof r.tag == "number" && E("Received an unexpected object in getComponentNameFromType(). This is likely a bug in React. Please file an issue."), typeof r == "function")
        return r.displayName || r.name || null;
      if (typeof r == "string")
        return r;
      switch (r) {
        case i:
          return "Fragment";
        case t:
          return "Portal";
        case p:
          return "Profiler";
        case o:
          return "StrictMode";
        case S:
          return "Suspense";
        case v:
          return "SuspenseList";
      }
      if (typeof r == "object")
        switch (r.$$typeof) {
          case l:
            var s = r;
            return je(s) + ".Consumer";
          case d:
            var a = r;
            return je(a._context) + ".Provider";
          case g:
            return kr(r, r.render, "ForwardRef");
          case y:
            var u = r.displayName || null;
            return u !== null ? u : A(r.type) || "Memo";
          case w: {
            var h = r, m = h._payload, f = h._init;
            try {
              return A(f(m));
            } catch {
              return null;
            }
          }
        }
      return null;
    }
    var D = Object.assign, Y = 0, De, Ue, Ne, Fe, Me, We, qe;
    function Le() {
    }
    Le.__reactDisabledLog = !0;
    function Sr() {
      {
        if (Y === 0) {
          De = console.log, Ue = console.info, Ne = console.warn, Fe = console.error, Me = console.group, We = console.groupCollapsed, qe = console.groupEnd;
          var r = {
            configurable: !0,
            enumerable: !0,
            value: Le,
            writable: !0
          };
          Object.defineProperties(console, {
            info: r,
            log: r,
            warn: r,
            error: r,
            group: r,
            groupCollapsed: r,
            groupEnd: r
          });
        }
        Y++;
      }
    }
    function br() {
      {
        if (Y--, Y === 0) {
          var r = {
            configurable: !0,
            enumerable: !0,
            writable: !0
          };
          Object.defineProperties(console, {
            log: D({}, r, {
              value: De
            }),
            info: D({}, r, {
              value: Ue
            }),
            warn: D({}, r, {
              value: Ne
            }),
            error: D({}, r, {
              value: Fe
            }),
            group: D({}, r, {
              value: Me
            }),
            groupCollapsed: D({}, r, {
              value: We
            }),
            groupEnd: D({}, r, {
              value: qe
            })
          });
        }
        Y < 0 && E("disabledDepth fell below zero. This is a bug in React. Please file an issue.");
      }
    }
    var de = I.ReactCurrentDispatcher, he;
    function te(r, s, a) {
      {
        if (he === void 0)
          try {
            throw Error();
          } catch (h) {
            var u = h.stack.trim().match(/\n( *(at )?)/);
            he = u && u[1] || "";
          }
        return `
` + he + r;
      }
    }
    var pe = !1, ne;
    {
      var Tr = typeof WeakMap == "function" ? WeakMap : Map;
      ne = new Tr();
    }
    function Ve(r, s) {
      if (!r || pe)
        return "";
      {
        var a = ne.get(r);
        if (a !== void 0)
          return a;
      }
      var u;
      pe = !0;
      var h = Error.prepareStackTrace;
      Error.prepareStackTrace = void 0;
      var m;
      m = de.current, de.current = null, Sr();
      try {
        if (s) {
          var f = function() {
            throw Error();
          };
          if (Object.defineProperty(f.prototype, "props", {
            set: function() {
              throw Error();
            }
          }), typeof Reflect == "object" && Reflect.construct) {
            try {
              Reflect.construct(f, []);
            } catch (T) {
              u = T;
            }
            Reflect.construct(r, [], f);
          } else {
            try {
              f.call();
            } catch (T) {
              u = T;
            }
            r.call(f.prototype);
          }
        } else {
          try {
            throw Error();
          } catch (T) {
            u = T;
          }
          r();
        }
      } catch (T) {
        if (T && u && typeof T.stack == "string") {
          for (var c = T.stack.split(`
`), b = u.stack.split(`
`), R = c.length - 1, k = b.length - 1; R >= 1 && k >= 0 && c[R] !== b[k]; )
            k--;
          for (; R >= 1 && k >= 0; R--, k--)
            if (c[R] !== b[k]) {
              if (R !== 1 || k !== 1)
                do
                  if (R--, k--, k < 0 || c[R] !== b[k]) {
                    var C = `
` + c[R].replace(" at new ", " at ");
                    return r.displayName && C.includes("<anonymous>") && (C = C.replace("<anonymous>", r.displayName)), typeof r == "function" && ne.set(r, C), C;
                  }
                while (R >= 1 && k >= 0);
              break;
            }
        }
      } finally {
        pe = !1, de.current = m, br(), Error.prepareStackTrace = h;
      }
      var W = r ? r.displayName || r.name : "", U = W ? te(W) : "";
      return typeof r == "function" && ne.set(r, U), U;
    }
    function Cr(r, s, a) {
      return Ve(r, !1);
    }
    function _r(r) {
      var s = r.prototype;
      return !!(s && s.isReactComponent);
    }
    function se(r, s, a) {
      if (r == null)
        return "";
      if (typeof r == "function")
        return Ve(r, _r(r));
      if (typeof r == "string")
        return te(r);
      switch (r) {
        case S:
          return te("Suspense");
        case v:
          return te("SuspenseList");
      }
      if (typeof r == "object")
        switch (r.$$typeof) {
          case g:
            return Cr(r.render);
          case y:
            return se(r.type, s, a);
          case w: {
            var u = r, h = u._payload, m = u._init;
            try {
              return se(m(h), s, a);
            } catch {
            }
          }
        }
      return "";
    }
    var B = Object.prototype.hasOwnProperty, Ye = {}, Be = I.ReactDebugCurrentFrame;
    function ie(r) {
      if (r) {
        var s = r._owner, a = se(r.type, r._source, s ? s.type : null);
        Be.setExtraStackFrame(a);
      } else
        Be.setExtraStackFrame(null);
    }
    function xr(r, s, a, u, h) {
      {
        var m = Function.call.bind(B);
        for (var f in r)
          if (m(r, f)) {
            var c = void 0;
            try {
              if (typeof r[f] != "function") {
                var b = Error((u || "React class") + ": " + a + " type `" + f + "` is invalid; it must be a function, usually from the `prop-types` package, but received `" + typeof r[f] + "`.This often happens because of typos such as `PropTypes.function` instead of `PropTypes.func`.");
                throw b.name = "Invariant Violation", b;
              }
              c = r[f](s, f, u, a, null, "SECRET_DO_NOT_PASS_THIS_OR_YOU_WILL_BE_FIRED");
            } catch (R) {
              c = R;
            }
            c && !(c instanceof Error) && (ie(h), E("%s: type specification of %s `%s` is invalid; the type checker function must return `null` or an `Error` but returned a %s. You may have forgotten to pass an argument to the type checker creator (arrayOf, instanceOf, objectOf, oneOf, oneOfType, and shape all require an argument).", u || "React class", a, f, typeof c), ie(null)), c instanceof Error && !(c.message in Ye) && (Ye[c.message] = !0, ie(h), E("Failed %s type: %s", a, c.message), ie(null));
          }
      }
    }
    var Ar = Array.isArray;
    function ge(r) {
      return Ar(r);
    }
    function Or(r) {
      {
        var s = typeof Symbol == "function" && Symbol.toStringTag, a = s && r[Symbol.toStringTag] || r.constructor.name || "Object";
        return a;
      }
    }
    function Ir(r) {
      try {
        return Ge(r), !1;
      } catch {
        return !0;
      }
    }
    function Ge(r) {
      return "" + r;
    }
    function Ke(r) {
      if (Ir(r))
        return E("The provided key is an unsupported type %s. This value must be coerced to a string before before using it here.", Or(r)), Ge(r);
    }
    var G = I.ReactCurrentOwner, Pr = {
      key: !0,
      ref: !0,
      __self: !0,
      __source: !0
    }, Je, ze, me;
    me = {};
    function $r(r) {
      if (B.call(r, "ref")) {
        var s = Object.getOwnPropertyDescriptor(r, "ref").get;
        if (s && s.isReactWarning)
          return !1;
      }
      return r.ref !== void 0;
    }
    function jr(r) {
      if (B.call(r, "key")) {
        var s = Object.getOwnPropertyDescriptor(r, "key").get;
        if (s && s.isReactWarning)
          return !1;
      }
      return r.key !== void 0;
    }
    function Dr(r, s) {
      if (typeof r.ref == "string" && G.current && s && G.current.stateNode !== s) {
        var a = A(G.current.type);
        me[a] || (E('Component "%s" contains the string ref "%s". Support for string refs will be removed in a future major release. This case cannot be automatically converted to an arrow function. We ask you to manually fix this case by using useRef() or createRef() instead. Learn more about using refs safely here: https://reactjs.org/link/strict-mode-string-ref', A(G.current.type), r.ref), me[a] = !0);
      }
    }
    function Ur(r, s) {
      {
        var a = function() {
          Je || (Je = !0, E("%s: `key` is not a prop. Trying to access it will result in `undefined` being returned. If you need to access the same value within the child component, you should pass it as a different prop. (https://reactjs.org/link/special-props)", s));
        };
        a.isReactWarning = !0, Object.defineProperty(r, "key", {
          get: a,
          configurable: !0
        });
      }
    }
    function Nr(r, s) {
      {
        var a = function() {
          ze || (ze = !0, E("%s: `ref` is not a prop. Trying to access it will result in `undefined` being returned. If you need to access the same value within the child component, you should pass it as a different prop. (https://reactjs.org/link/special-props)", s));
        };
        a.isReactWarning = !0, Object.defineProperty(r, "ref", {
          get: a,
          configurable: !0
        });
      }
    }
    var Fr = function(r, s, a, u, h, m, f) {
      var c = {
        // This tag allows us to uniquely identify this as a React Element
        $$typeof: e,
        // Built-in properties that belong on the element
        type: r,
        key: s,
        ref: a,
        props: f,
        // Record the component responsible for creating this element.
        _owner: m
      };
      return c._store = {}, Object.defineProperty(c._store, "validated", {
        configurable: !1,
        enumerable: !1,
        writable: !0,
        value: !1
      }), Object.defineProperty(c, "_self", {
        configurable: !1,
        enumerable: !1,
        writable: !1,
        value: u
      }), Object.defineProperty(c, "_source", {
        configurable: !1,
        enumerable: !1,
        writable: !1,
        value: h
      }), Object.freeze && (Object.freeze(c.props), Object.freeze(c)), c;
    };
    function Mr(r, s, a, u, h) {
      {
        var m, f = {}, c = null, b = null;
        a !== void 0 && (Ke(a), c = "" + a), jr(s) && (Ke(s.key), c = "" + s.key), $r(s) && (b = s.ref, Dr(s, h));
        for (m in s)
          B.call(s, m) && !Pr.hasOwnProperty(m) && (f[m] = s[m]);
        if (r && r.defaultProps) {
          var R = r.defaultProps;
          for (m in R)
            f[m] === void 0 && (f[m] = R[m]);
        }
        if (c || b) {
          var k = typeof r == "function" ? r.displayName || r.name || "Unknown" : r;
          c && Ur(f, k), b && Nr(f, k);
        }
        return Fr(r, c, b, h, u, G.current, f);
      }
    }
    var ve = I.ReactCurrentOwner, Qe = I.ReactDebugCurrentFrame;
    function M(r) {
      if (r) {
        var s = r._owner, a = se(r.type, r._source, s ? s.type : null);
        Qe.setExtraStackFrame(a);
      } else
        Qe.setExtraStackFrame(null);
    }
    var ye;
    ye = !1;
    function we(r) {
      return typeof r == "object" && r !== null && r.$$typeof === e;
    }
    function Xe() {
      {
        if (ve.current) {
          var r = A(ve.current.type);
          if (r)
            return `

Check the render method of \`` + r + "`.";
        }
        return "";
      }
    }
    function Wr(r) {
      {
        if (r !== void 0) {
          var s = r.fileName.replace(/^.*[\\\/]/, ""), a = r.lineNumber;
          return `

Check your code at ` + s + ":" + a + ".";
        }
        return "";
      }
    }
    var He = {};
    function qr(r) {
      {
        var s = Xe();
        if (!s) {
          var a = typeof r == "string" ? r : r.displayName || r.name;
          a && (s = `

Check the top-level render call using <` + a + ">.");
        }
        return s;
      }
    }
    function Ze(r, s) {
      {
        if (!r._store || r._store.validated || r.key != null)
          return;
        r._store.validated = !0;
        var a = qr(s);
        if (He[a])
          return;
        He[a] = !0;
        var u = "";
        r && r._owner && r._owner !== ve.current && (u = " It was passed a child from " + A(r._owner.type) + "."), M(r), E('Each child in a list should have a unique "key" prop.%s%s See https://reactjs.org/link/warning-keys for more information.', a, u), M(null);
      }
    }
    function er(r, s) {
      {
        if (typeof r != "object")
          return;
        if (ge(r))
          for (var a = 0; a < r.length; a++) {
            var u = r[a];
            we(u) && Ze(u, s);
          }
        else if (we(r))
          r._store && (r._store.validated = !0);
        else if (r) {
          var h = fe(r);
          if (typeof h == "function" && h !== r.entries)
            for (var m = h.call(r), f; !(f = m.next()).done; )
              we(f.value) && Ze(f.value, s);
        }
      }
    }
    function Lr(r) {
      {
        var s = r.type;
        if (s == null || typeof s == "string")
          return;
        var a;
        if (typeof s == "function")
          a = s.propTypes;
        else if (typeof s == "object" && (s.$$typeof === g || // Note: Memo only checks outer props here.
        // Inner props are checked in the reconciler.
        s.$$typeof === y))
          a = s.propTypes;
        else
          return;
        if (a) {
          var u = A(s);
          xr(a, r.props, "prop", u, r);
        } else if (s.PropTypes !== void 0 && !ye) {
          ye = !0;
          var h = A(s);
          E("Component %s declared `PropTypes` instead of `propTypes`. Did you misspell the property assignment?", h || "Unknown");
        }
        typeof s.getDefaultProps == "function" && !s.getDefaultProps.isReactClassApproved && E("getDefaultProps is only used on classic React.createClass definitions. Use a static property named `defaultProps` instead.");
      }
    }
    function Vr(r) {
      {
        for (var s = Object.keys(r.props), a = 0; a < s.length; a++) {
          var u = s[a];
          if (u !== "children" && u !== "key") {
            M(r), E("Invalid prop `%s` supplied to `React.Fragment`. React.Fragment can only have `key` and `children` props.", u), M(null);
            break;
          }
        }
        r.ref !== null && (M(r), E("Invalid attribute `ref` supplied to `React.Fragment`."), M(null));
      }
    }
    var rr = {};
    function tr(r, s, a, u, h, m) {
      {
        var f = Er(r);
        if (!f) {
          var c = "";
          (r === void 0 || typeof r == "object" && r !== null && Object.keys(r).length === 0) && (c += " You likely forgot to export your component from the file it's defined in, or you might have mixed up default and named imports.");
          var b = Wr(h);
          b ? c += b : c += Xe();
          var R;
          r === null ? R = "null" : ge(r) ? R = "array" : r !== void 0 && r.$$typeof === e ? (R = "<" + (A(r.type) || "Unknown") + " />", c = " Did you accidentally export a JSX literal instead of a component?") : R = typeof r, E("React.jsx: type is invalid -- expected a string (for built-in components) or a class/function (for composite components) but got: %s.%s", R, c);
        }
        var k = Mr(r, s, a, h, m);
        if (k == null)
          return k;
        if (f) {
          var C = s.children;
          if (C !== void 0)
            if (u)
              if (ge(C)) {
                for (var W = 0; W < C.length; W++)
                  er(C[W], r);
                Object.freeze && Object.freeze(C);
              } else
                E("React.jsx: Static children should always be an array. You are likely explicitly calling React.jsxs or React.jsxDEV. Use the Babel transform instead.");
            else
              er(C, r);
        }
        if (B.call(s, "key")) {
          var U = A(r), T = Object.keys(s).filter(function(zr) {
            return zr !== "key";
          }), Re = T.length > 0 ? "{key: someKey, " + T.join(": ..., ") + ": ...}" : "{key: someKey}";
          if (!rr[U + Re]) {
            var Jr = T.length > 0 ? "{" + T.join(": ..., ") + ": ...}" : "{}";
            E(`A props object containing a "key" prop is being spread into JSX:
  let props = %s;
  <%s {...props} />
React keys must be passed directly to JSX without using spread:
  let props = %s;
  <%s key={someKey} {...props} />`, Re, U, Jr, U), rr[U + Re] = !0;
          }
        }
        return r === i ? Vr(k) : Lr(k), k;
      }
    }
    function Yr(r, s, a) {
      return tr(r, s, a, !0);
    }
    function Br(r, s, a) {
      return tr(r, s, a, !1);
    }
    var Gr = Br, Kr = Yr;
    Q.Fragment = i, Q.jsx = Gr, Q.jsxs = Kr;
  }()), Q;
}
process.env.NODE_ENV === "production" ? xe.exports = St() : xe.exports = bt();
var X = xe.exports;
const Tt = x("SettingsErrorBoundary");
class nn extends et {
  constructor(e) {
    super(e), this.handleRetry = () => {
      this.setState({ error: void 0 }), this.props.onRetry ? this.props.onRetry() : window.location.reload();
    }, this.state = { error: void 0 };
  }
  static getDerivedStateFromError(e) {
    return e instanceof _ ? { error: e } : { error: void 0 };
  }
  componentDidCatch(e, t) {
    e instanceof _ && Tt.error("Settings error:", e, t);
  }
  render() {
    return this.state.error ? /* @__PURE__ */ X.jsx("div", { style: { padding: "2rem", maxWidth: "600px", margin: "0 auto" }, children: /* @__PURE__ */ X.jsxs(tt, { open: !0, kind: "danger", autoClose: !1, children: [
      /* @__PURE__ */ X.jsx("div", { slot: "title", children: "Kunne ikke laste innstillinger" }),
      /* @__PURE__ */ X.jsx("div", { slot: "message", children: this.state.error.toUserMessage() }),
      /* @__PURE__ */ X.jsx(nt, { slot: "actions-end", onClick: this.handleRetry, children: "Prøv igjen" })
    ] }) }) : this.props.children;
  }
}
function sn() {
  const [n, e] = Z.useState(), t = Z.useCallback((o) => {
    o instanceof _ && e(o);
  }, []), i = Z.useCallback(() => {
    e(void 0);
  }, []);
  return { error: n, handleError: t, clearError: i };
}
const an = rt({
  auth: ot,
  token: dt,
  settings: yt
});
export {
  or as AuthService,
  lr as GdoTokenProvider,
  Ft as PortalItemService,
  _ as SettingsError,
  nn as SettingsErrorBoundary,
  ur as SettingsService,
  an as authCombinedReducer,
  ot as authReducer,
  it as buildSettingsQueryString,
  st as buildTokenServiceQueryString,
  oe as checkSignInStatus,
  Wt as clearAuth,
  Bt as clearToken,
  Pe as getCustomerDomain,
  ce as getToken,
  _e as loadSettings,
  Se as refreshToken,
  ue as requireSignIn,
  Qt as selectApplicationRoles,
  qt as selectAuthError,
  ct as selectAuthStatus,
  Ht as selectGdoCustomerName,
  zt as selectGdoToken,
  Xt as selectGdoUsername,
  Lt as selectIsAuthRequired,
  ut as selectIsSignedIn,
  Kt as selectIsTokenExpired,
  pt as selectIsTokenRefreshing,
  wt as selectSettings,
  Et as selectSettingsError,
  Rt as selectSettingsLoading,
  ht as selectToken,
  Jt as selectTokenError,
  Gt as selectTokenExpiration,
  lt as selectUser,
  Yt as setAppName,
  Mt as setIsAuthRequired,
  Zt as setSettings,
  Vt as setToken,
  nr as setTokenMetadata,
  yt as settingsReducer,
  le as signOut,
  dt as tokenReducer,
  fr as useAuth,
  tn as useAuthenticatedApp,
  rn as usePublicApp,
  mr as useSettings,
  sn as useSettingsErrorHandler,
  pr as useTokenProvider,
  en as useTokenRegistration
};
//# sourceMappingURL=index.mjs.map
